import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { useConfirm, Modal, Button, Input, Tooltip } from './ui';

/**
 * Modal genérico para gestionar categorías (CRUD inline).
 * Reutilizado por: plantillas, servicios, costos fijos.
 *
 * Props:
 *   - open, onClose, onChanged → control del modal y notificación al padre
 *   - title → título del modal
 *   - api → objeto con métodos { list, create, update, delete }
 *           list() → { data: [{id, nombre, [countField]}] }
 *           create(data), update(id, data), delete(id)
 *   - countField → nombre del campo en el item que tiene el contador
 *                  (ej: 'plantillas_count', 'tipos_count')
 *   - countLabel → singular del label (ej: 'plantilla' → "1 plantilla", "2 plantillas")
 *   - placeholder → placeholder del input "Nueva categoría"
 */
const CategoriasManagerModal = ({
    open,
    onClose,
    onChanged,
    title = 'Gestionar categorías',
    api,
    countField = 'count',
    countLabel = 'item',
    placeholder = 'Ej: Nueva categoría'
}) => {
    const confirm = useConfirm();
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nuevoNombre, setNuevoNombre] = useState('');
    const [editandoId, setEditandoId] = useState(null);
    const [editNombre, setEditNombre] = useState('');
    const [saving, setSaving] = useState(false);
    const [huboCambios, setHuboCambios] = useState(false);

    useEffect(() => {
        if (open) cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await api.list();
            setCategorias(res.data || []);
        } catch {
            toast.error('Error al cargar categorías');
        } finally {
            setLoading(false);
        }
    };

    const crear = async () => {
        if (!nuevoNombre.trim()) return;
        setSaving(true);
        try {
            await api.create({ nombre: nuevoNombre.trim() });
            toast.success('Categoría creada');
            setNuevoNombre('');
            setHuboCambios(true);
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error al crear');
        } finally {
            setSaving(false);
        }
    };

    const iniciarEdicion = (cat) => {
        setEditandoId(cat.id);
        setEditNombre(cat.nombre);
    };

    const cancelarEdicion = () => {
        setEditandoId(null);
        setEditNombre('');
    };

    const guardarEdicion = async () => {
        if (!editNombre.trim()) return;
        setSaving(true);
        try {
            await api.update(editandoId, { nombre: editNombre.trim() });
            toast.success('Categoría actualizada');
            setHuboCambios(true);
            cancelarEdicion();
            cargar();
        } catch (err) {
            toast.error(err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const eliminar = async (cat) => {
        const count = cat[countField] || 0;
        const ok = await confirm({
            title: '¿Eliminar categoría?',
            message: (
                <p>
                    Vas a eliminar <strong>{cat.nombre}</strong>.
                    {count > 0 && (
                        <> Los <strong>{count}</strong> {count === 1 ? countLabel : `${countLabel}s`} que la usan quedarán sin categoría.</>
                    )}
                </p>
            ),
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            await api.delete(cat.id);
            toast.success('Categoría eliminada');
            setHuboCambios(true);
            cargar();
        } catch {
            toast.error('Error al eliminar');
        }
    };

    const cerrar = () => {
        if (huboCambios && onChanged) onChanged();
        setHuboCambios(false);
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={cerrar}
            title={title}
            size="lg"
            footer={
                <div className="flex justify-end w-full">
                    <Button variant="ghost" onClick={cerrar}>Cerrar</Button>
                </div>
            }
        >
            <div className="flex flex-col gap-4">
                {/* Crear nueva */}
                <div className="flex gap-2 items-end">
                    <Input
                        id="cat-nueva"
                        label="Nueva categoría"
                        type="text"
                        value={nuevoNombre}
                        onChange={(e) => setNuevoNombre(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && nuevoNombre.trim()) crear(); }}
                        placeholder={placeholder}
                        wrapperClassName="flex-1"
                    />
                    <Button
                        variant="primary"
                        leftIcon={<Plus size={14} />}
                        onClick={crear}
                        disabled={!nuevoNombre.trim() || saving}
                    >
                        Agregar
                    </Button>
                </div>

                {/* Lista */}
                <div>
                    <div className="text-xs text-muted font-medium uppercase tracking-wide mb-2">
                        {categorias.length} {categorias.length === 1 ? 'categoría existente' : 'categorías existentes'}
                    </div>

                    {loading ? (
                        <div className="text-center text-muted py-4 text-sm">Cargando…</div>
                    ) : categorias.length === 0 ? (
                        <div className="text-center text-muted py-6 text-sm">
                            Aún no hay categorías. Crea una arriba.
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1.5">
                            {categorias.map(cat => {
                                const count = cat[countField] || 0;
                                return (
                                    <div
                                        key={cat.id}
                                        className="flex items-center gap-2 p-2 rounded-lg border border-line bg-surface hover:bg-surface-2 transition"
                                    >
                                        {editandoId === cat.id ? (
                                            <>
                                                <Input
                                                    id={`cat-edit-${cat.id}`}
                                                    type="text"
                                                    value={editNombre}
                                                    onChange={(e) => setEditNombre(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') guardarEdicion();
                                                        if (e.key === 'Escape') cancelarEdicion();
                                                    }}
                                                    autoFocus
                                                    wrapperClassName="flex-1"
                                                />
                                                <Tooltip content="Guardar">
                                                    <button
                                                        type="button"
                                                        className="btn-icon version"
                                                        onClick={guardarEdicion}
                                                        disabled={saving || !editNombre.trim()}
                                                        aria-label="Guardar"
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip content="Cancelar">
                                                    <button
                                                        type="button"
                                                        className="btn-icon"
                                                        onClick={cancelarEdicion}
                                                        disabled={saving}
                                                        aria-label="Cancelar"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <>
                                                <span className="flex-1 text-sm text-text font-medium px-2">{cat.nombre}</span>
                                                <span className="text-[11px] text-muted">
                                                    {count} {count === 1 ? countLabel : `${countLabel}s`}
                                                </span>
                                                <Tooltip content="Editar categoría">
                                                    <button
                                                        type="button"
                                                        className="btn-icon edit"
                                                        onClick={() => iniciarEdicion(cat)}
                                                        aria-label="Editar"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip content="Eliminar categoría">
                                                    <button
                                                        type="button"
                                                        className="btn-icon delete"
                                                        onClick={() => eliminar(cat)}
                                                        aria-label="Eliminar"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </Tooltip>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default CategoriasManagerModal;
